import moment from 'moment';
import { parseJSON } from './json';
import { API_ENDPOINT } from '../constants';
import { MAX_RANGE_DAYS } from './externalApiStats';

/**
* Data access + period/color helpers for the global "API analytics" page
* (VD-725): the long-range `/overview` endpoint (daily counters, up to a
* year, compared to the previous period of the same length) and the shared
* period picker that feeds both it and the minute-level stats/insights/logs
* tabs.
*/

export const MAX_OVERVIEW_DAYS = 366;
export const OVERVIEW_TOP_CLIENTS = 8;
export const CLIENT_SERIES_COUNT = 8;

export const PAGE_PERIODS = [
    { value: '24h', label: 'Last 24 hours', days: 1 },
    { value: '7d', label: 'Last 7 days', days: 7 },
    { value: '30d', label: 'Last 30 days', days: 30 },
    { value: '90d', label: 'Last 90 days', days: 90 },
    { value: '365d', label: 'Last 365 days', days: 365 },
    { value: 'custom', label: 'Custom' },
];

/**
* {from, to} ISO strings for the minute-level tabs (stats/insights/logs), plus
* how many days that spans so the page can tell when it is past their limit.
* Returns null for an incomplete custom range.
*/
export const detailRange = ({ preset, customFrom, customTo }) => {
    if (preset === 'custom') {
        if (!customFrom || !customTo) return null;
        let from = moment(customFrom);
        let to = moment(customTo);
        return { from: from.toISOString(), to: to.toISOString(), days: to.diff(from, 'days', true) };
    }

    let def = PAGE_PERIODS.find((p) => p.value === preset) || PAGE_PERIODS[1];
    let to = moment();
    let from = moment(to).subtract(def.days * 24, 'hours');

    return { from: from.toISOString(), to: to.toISOString(), days: def.days };
};

export const isDetailRangeTooLong = (range) => !!range && range.days > MAX_RANGE_DAYS;

/** {from, to} as inclusive UTC `YYYY-MM-DD` days for `/overview` */
export const overviewRange = ({ preset, customFrom, customTo }) => {
    if (preset === 'custom') {
        if (!customFrom || !customTo) return null;
        let from = moment(customFrom).format('YYYY-MM-DD');
        let to = moment(customTo).format('YYYY-MM-DD');
        return { from, to, days: moment.utc(to).diff(moment.utc(from), 'days') + 1 };
    }

    let def = PAGE_PERIODS.find((p) => p.value === preset) || PAGE_PERIODS[1];
    let to = moment.utc();
    let from = moment.utc(to).subtract(def.days - 1, 'days');

    return { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD'), days: def.days };
};

/**
* The client's own analytics tab only does up to 31 days, so carry the page's
* period over as closely as it allows: same preset where one exists, the last
* 30 days for the longer presets, and a custom range clamped to its last 31
* days.
*/
export const clientTabPeriod = ({ preset, customFrom, customTo }) => {
    if (preset === '24h' || preset === '7d' || preset === '30d') {
        return { preset };
    }

    if (preset === 'custom' && customFrom && customTo) {
        let to = moment(customTo);
        let from = moment.max(moment(customFrom), moment(to).subtract(MAX_RANGE_DAYS, 'days'));
        return { preset: 'custom', customFrom: from.format('YYYY-MM-DDTHH:mm'), customTo: to.format('YYYY-MM-DDTHH:mm') };
    }

    return { preset: '30d' };
};

/**
* URL of a client's "API analytics" tab on the Customers API usage page.
* There is no "get client by id" route, so it lands on the list pre-filtered
* to that client and lets the page open the matching row once it loads.
*/
export const clientAnalyticsUrl = ({ clientId, customerNumber, username, name }, period) => {
    let qs = new URLSearchParams();
    qs.set('filter', customerNumber || username || name || '');
    qs.set('focusClientId', clientId);
    qs.set('focusClientTab', 'apiAnalytics');

    let p = clientTabPeriod(period || {});
    qs.set('focusPreset', p.preset);
    if (p.customFrom) qs.set('focusFrom', p.customFrom);
    if (p.customTo) qs.set('focusTo', p.customTo);

    return `/customers-api-usage?${qs.toString()}`;
};

/**
* Categorical slots 1-8 in their fixed, validated order (dataviz reference
* palette - passes the adjacent-pair CVD and normal-vision checks, which is
* what a stacked chart needs). A 9th client never gets a generated hue, it
* folds into "Other", drawn in a neutral gray.
*/
export const CLIENT_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
export const OTHER_COLOR = '#B4B2A9';

/**
* Color follows the client, not its rank: the first time a client shows up
* in any stacked chart on the page it takes the next free slot and keeps it,
* so the same client is the same color on Overview and Load even though the
* two endpoints rank their top N separately.
*/
export const createClientColorRegistry = () => {
    let slots = {};
    let next = 0;

    return (clientId) => {
        if (!clientId) return OTHER_COLOR;
        if (slots[clientId] === undefined) {
            slots[clientId] = next % CLIENT_COLORS.length;
            next += 1;
        }
        return CLIENT_COLORS[slots[clientId]];
    };
};

export const clientDisplayName = (client) => {
    if (!client) return '-';
    if (typeof client === 'string') return client;
    return client.name || client.company || client.username || client.clientId || '-';
};

/** @returns {Promise<{data: object|null, error: string|null}>} */
export const fetchExternalApiOverview = ({ from, to, top }) => {
    let qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (top) qs.set('top', top);

    return fetch(`${API_ENDPOINT}/data/external-api/overview?${qs.toString()}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
    }).then(parseJSON).then(({ result, status }) => {
        if (status >= 200 && status < 300) {
            return { data: result, error: null };
        }

        // the route is new, so say plainly when the backend simply doesn't have it yet
        if (status === 404) {
            return { data: null, error: 'The backend has no GET /data/external-api/overview route (404) - the Overview tab works once a backend with that route is running.' };
        }

        if (status === 401 || status === 403) {
            return { data: null, error: `No permission for GET /data/external-api/overview (${status}) - the route has not been added to the "Customers" module yet (run the modules script).` };
        }

        return { data: null, error: result && result.error ? result.error : 'Failed to load overview' };
    });
};
